import { uniqBy } from 'lodash-es';
import { Cart } from '@lambda/commons/apis/shopify/types/storefront';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import { Logger } from '@lambda/commons/logging/logger';
import { RC_DURATION } from '@lambda/reebelo';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import {
  CartItemAttribute,
  generateIDAttr,
  encodeDecodeForAttr,
  generateAddCartDateTimeAttr,
} from '@/lib/use-cart';

import {
  RecommendationsT,
  AddToCartArgs,
  QtyChangeArgs,
  RecommendedItemT,
  RemoveFromCartArgs,
  AddToCartExpressShippingArgs,
} from './types';
import {
  filterOutShippingDeliveryAttributes,
} from '@/lib/order';
import settings from '@/settings';
import { trackReebeloCareAdded } from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import { logOutOfStockCartAddition, createOutOfStockLogData } from '@/lib/cart-out-of-stock-logger';

const GID_VAR = 'gid://shopify/ProductVariant/';
const getMerchandiseId = (variantId: number) => `${GID_VAR}${variantId}`;
const logger = new Logger('v2-card-recommendedItemsUtils');

export const handleAddToCart =
  ({
    add,
    loading,
    quantity,
    variantId,
    attributes,
    setScrollPosOnCartChange,
    callback,
    type,
    parentItem,
    title,
    vendor,
    itemSku,
    itemPsku,
    price,
    itemCondition,
    itemColor,
    itemBrand,
    itemStorage,
    itemCategory,
    cartSize,
    itemPosition,
    accessoryModalSource,
  }: AddToCartArgs): (() => void) =>
  () => {
    if (loading || !variantId) return;

    const merchandiseId = getMerchandiseId(variantId);

    if (setScrollPosOnCartChange) setScrollPosOnCartChange();
    
    // Check stock availability before adding to cart
    const stockAttribute = attributes?.find(attr => attr.key === 'itemStock');
    const availableStock = stockAttribute ? parseInt(stockAttribute.value, 10) : undefined;
    
    if (availableStock !== undefined && availableStock < quantity) {
      // Log out-of-stock scenario
      const productData = {
        sku: itemSku || 'unknown',
        psku: itemPsku || 'unknown',
        title: title || 'Unknown Product',
        vendor: vendor || 'unknown',
        price: price || 0,
        condition: itemCondition,
        color: itemColor,
        brand: itemBrand,
        storage: itemStorage,
        category: itemCategory,
      };

      const cartData = {
        quantity,
        cartId: undefined, // Will be available after cart update
        cartSize,
        itemPosition,
        accessoryModalSource: accessoryModalSource || false,
      };

      const stockData = {
        availableStock,
        requestedQuantity: quantity,
      };

      const logData = createOutOfStockLogData(productData, cartData, stockData);
      logOutOfStockCartAddition(logData);

      logger.warn({
        product: productData,
        cart: cartData,
        stock: stockData,
        variantId,
      }, 'Attempted to add out-of-stock item to cart');
    }

    add({
      quantity,
      attributes,
      merchandiseId,
    }).then((cart) => {
      if (callback) callback();

      if (type === 'warranty') {
        const { psku } = extractTags(
          parentItem?.merchandise.product.tags ?? [],
        );
        const trackingProps: ReebeloCareTrackRequest = {
          entry_point: 'Cart',
          reebelocare_id: variantId,
          device_psku: psku ?? '',
          device_vendor: parentItem?.merchandise.product.vendor ?? '',
          device_sku: parentItem?.merchandise.sku ?? '',
          duration: RC_DURATION['24_MONTH'],
        };

        trackReebeloCareAdded(trackingProps);
      }

      // Check if the cart response indicates stock issues
      // Note: The cart object is from Shopify, so we need to check for stock issues differently
      // The stock validation happens in the backend cart service, so we'll log based on the initial stock check
      if (cart && typeof cart === 'object' && 'lines' in cart) {
        // For Shopify cart, we can't directly access stock information from the response
        // The stock validation happens in the backend cart service
        // We've already logged the potential out-of-stock scenario above if stock was insufficient
        
        // Additional logging for successful cart addition with stock concerns
        if (availableStock !== undefined && availableStock < quantity) {
          logger.warn({
            cartId: cart.id,
            variantId,
            quantity,
            availableStock,
            product: {
              title,
              vendor,
              sku: itemSku,
              psku: itemPsku,
            },
          }, 'Item added to cart despite insufficient stock - backend validation may catch this');
        }
      }
    }).catch((error) => {
      logger.error({
        error,
        variantId,
        quantity,
        attributes,
        product: {
          title,
          vendor,
          sku: itemSku,
          psku: itemPsku,
        },
      }, 'Failed to add item to cart');
    });
  };

export const handleAddToCartExpressShipping =
  ({
    add,
    update,
    price,
    title,
    loading,
    quantity,
    variantId,
    dispatchMin,
    dispatchMax,
    minDeliveryDate,
    maxDeliveryDate,
    cutoffTime,
    cutoffTimezone,
    zipcodeStateData,
    parentItem,
    setScrollPosOnCartChange,
  }: AddToCartExpressShippingArgs): (() => void) =>
  async () => {
    try {
      if (loading)
        throw new Error('not adding express shipping to cart -- still loading');

      if (!variantId) {
        throw new Error(
          'not adding express shipping to cart -- missing variant ID',
        );
      }

      if (!parentItem) {
        throw new Error(
          'not adding express shipping to cart -- missing parent ID',
        );
      }

      const merchandiseId = getMerchandiseId(variantId);
      const shippingAttributes = [
        { key: '_shipping_option', value: 'express' },
        {
          key: '_shipping_option_cost',
          value: price.toString(),
        },
      ];
      const deliverByAttributes = [
        {
          key: '_deliver_min',
          value: minDeliveryDate,
        },
        {
          key: '_deliver_max',
          value: maxDeliveryDate,
        },
        {
          key: '_cutoff_time',
          value: cutoffTime == null || cutoffTime === '' ? '' : cutoffTime,
        },
        {
          key: '_cutoff_timezone',
          value:
            cutoffTimezone == null || cutoffTimezone === ''
              ? ''
              : cutoffTimezone,
        },
        ...(settings.store === 'reebelo-au'
          ? [
              {
                key: '_deliver_state',
                value: zipcodeStateData?.state.toString() || '',
              },
              {
                key: '_deliver_zipcode',
                value: zipcodeStateData?.zipcode.toString() || '',
              },
            ]
          : []),
      ];

      if (setScrollPosOnCartChange) setScrollPosOnCartChange();

      // add express shipping line
      await add({
        quantity,
        attributes: [
          ...encodeDecodeForAttr.encode({
            sku: parentItem.merchandise.sku,
            title: parentItem.merchandise.product.title,
          }),
          ...generateIDAttr(
            parentItem.merchandise.product.vendor,
            parentItem.merchandise.sku,
          ),
          ...generateAddCartDateTimeAttr(),
        ],
        merchandiseId,
      });

      const filteredAttributes = filterOutShippingDeliveryAttributes(
        parentItem.attributes,
      );

      // update parent delivery attributes based on express shippping attribute
      await update([
        {
          attributes: [
            ...filteredAttributes,
            ...deliverByAttributes,
            ...shippingAttributes,
            { key: '_dispatch_min', value: dispatchMin.toString() },
            { key: '_dispatch_max', value: dispatchMax.toString() },
          ],
          id: parentItem.id,
          quantity: parentItem.quantity,
        },
      ]);
    } catch (error: any) {
      logger.error('error adding express shipping to cart', {
        title,
        price,
        quantity,
        variantId,
        parentItem,
        error,
      });
    }
  };

export const handleRemoveFromCart =
  ({
    id,
    remove,
    loading,
    setScrollPosOnCartChange,
  }: RemoveFromCartArgs): (() => void) =>
  async () => {
    if (loading || !id) return;

    if (setScrollPosOnCartChange) setScrollPosOnCartChange();

    remove([id]);
  };

const partitionArray = <T>(
  array: Array<T>,
  index: number,
): [Array<T>, Array<T>] => [array.slice(0, index), array.slice(index)];

export const makeRecommendedItemsSeq = (
  items: RecommendationsT,
): Array<SearchOffers> => {
  const { headphone, deviceCase, accessory, screenProtector, general } = items;

  const [firstHeadPhone, restOfHeadPhones] = partitionArray(headphone, 1);
  const [firstScreenProtector, restOfScreenProtectors] = partitionArray(
    screenProtector,
    1,
  );
  const [first4Cases, restOfCases] = partitionArray(deviceCase, 4);
  const [first2Accessories, restOfAccessories] = partitionArray(accessory, 2);

  const recommendedItems = [
    ...firstScreenProtector,
    ...firstHeadPhone,
    ...first4Cases,
    ...first2Accessories,
    ...restOfCases,
    ...restOfAccessories,
    ...restOfHeadPhones,
    ...restOfScreenProtectors,
    ...general,
  ].filter(Boolean);

  return recommendedItems;
};

export const mergeAllPopularItems = (recommendedItems?: RecommendedItemT[]) => {
  if (!recommendedItems) return [];

  return uniqBy(
    recommendedItems.reduce(
      (prev, { recommendations: { popular } }) => [...prev, ...popular],
      [] as SearchOffers[],
    ),
    'title',
  );
};

export const handleQtyChange =
  ({ lineId, update, availableQty, lineItem }: QtyChangeArgs) =>
  (value: number | string) => {
    const newValue =
      Number(value) > availableQty ? availableQty : Number(value);

    // Log if user tried to exceed available stock
    if (Number(value) > availableQty && lineItem) {
      const productData = {
        sku: lineItem.merchandise.sku,
        psku: lineItem.merchandise.product.tags?.find(tag => tag.includes('psku:'))?.split(':')[1] || 'unknown',
        title: lineItem.merchandise.product.title,
        vendor: lineItem.merchandise.product.vendor,
        price: lineItem.merchandise.priceV2?.amount || 0,
        condition: undefined,
        color: undefined,
        brand: undefined,
        storage: undefined,
        category: undefined,
      };

      const cartData = {
        quantity: Number(value),
        cartId: undefined,
        cartSize: undefined,
        itemPosition: undefined,
        accessoryModalSource: false,
      };

      const stockData = {
        availableStock: availableQty,
        requestedQuantity: Number(value),
      };

      const logData = createOutOfStockLogData(productData, cartData, stockData);
      logOutOfStockCartAddition(logData);

      logger.warn({
        lineId,
        availableQty,
        requestedQty: Number(value),
        product: {
          title: lineItem.merchandise.product.title,
          vendor: lineItem.merchandise.product.vendor,
          sku: lineItem.merchandise.sku,
        },
      }, 'User attempted to increase quantity beyond available stock');
    }

    if (newValue < 0) return;

    const normalizedFieldValue = value === 0 ? '' : newValue;

    if (
      typeof normalizedFieldValue === 'number' &&
      typeof newValue === 'number' &&
      lineId != null &&
      lineItem != null
    ) {
      const updates = [
        {
          id: lineId,
          quantity: newValue,
          attributes: lineItem.attributes as Array<CartItemAttribute>,
        },
      ];

      update(updates);
    }
  };

type GetFromCartType = {
  cart: Cart;
  recommendedItem: SearchOffers;
};

export const getItemFromCart = ({ cart, recommendedItem }: GetFromCartType) => {
  const {
    lines: { edges },
  } = cart;
  const nodes = edges.map((edge) => edge.node);

  const [selectedItem] = nodes.filter(
    ({ merchandise }) => merchandise.product.title === recommendedItem.title,
  );

  return selectedItem || {};
};

export const getItemQtyInCart = ({
  cart,
  recommendedItem,
}: GetFromCartType) => {
  const selectedItem = getItemFromCart({ cart, recommendedItem });

  if (selectedItem.quantity) return selectedItem.quantity;

  return 0;
};
